import {BlockItemDrawProps} from "../FrameBlockItem";
import {getSwingSide} from "../../frameProfile/Helpers";
import {Container, Graphics, Ticker} from "pixi.js";
import {ElevationApp} from "../../../ElevationApp";
import {inches, lineBlueHeavy, lineRed, lineRedGrey, panelMetalGrey} from "../../../const";
import {LinearDimension} from "../LinearDimension";
import {DoorLiteThing} from "../../doorElev/DoorLiteThing";
import {HardwareCrosshairThing} from "../../hardware/HardwareCrosshairThing";
import {placedOnLeaf} from "../../hardware/hardwarePreps";
import {MdSelection} from "../../../model/view/MdViewSettings";
import {SlotLabel} from "./SlotLabel";
import {drawDashedRect} from "../../../shared-2d/dashed";

// hinge knuckle drawn straddling the door's hinge edge (scene units = mm).
const HINGE_WIDTH = 1/2 * inches;

// how far below the leaf tops the horizontal pair-gap dimension sits.
const PAIR_GAP_DIM_OFFSET = 6 * inches;

// how far in from the leaf's left edge the vertical threshold dimension sits.
const THRESHOLD_DIM_OFFSET = 6 * inches;

// hover swing preview: how far the ghost leaf opens (radians), how much the free
// edge grows vertically per unit of horizontal swing (a pseudo-3d hint that the
// leaf comes toward the viewer), and the easing time constant toward the open /
// closed position (ms).
const SWING_MAX_ANGLE = Math.PI / 6;
const SWING_PERSPECTIVE = 0.18;
const SWING_EASE_MS = 120;

// the swing-preview trigger badge: radius, and its inset from the leaf's top
// hinge corner. The vertical inset clears the cell's width dimension (drawn
// DIM_INSET below the cell top).
const SWING_ICON_RADIUS = 2.5 * inches;
const SWING_ICON_MARGIN_X = 1 * inches;
const SWING_ICON_MARGIN_Y = 6 * inches;

// which leaf of an opening a DoorLeafFace hosts. Single openings only ever use
// "active"; a pair uses both. Doubles as the key litesFor() takes.
type LeafKind = "active" | "inactive";

// the leaf-local placement the hosted lite/hardware Things need, refreshed each
// draw. `left`/`top` are the leaf's top-left in the cell's coords (the Thing
// graphics are positioned there); `width`/`height` size the leaf for the Things'
// dimension extents.
type LeafGeom = {
    left: number;
    top: number;
    width: number;
    height: number;
};

// the resolved on-screen extents of one door leaf, returned by renderDoor so the
// caller can dimension the gap between a pair and the clearance under the sill.
export type LeafRect = {
    doorLeft: number;
    doorRight: number;
    doorTop: number;
    doorBottom: number;
    // the undercut trimmed off the bottom of the leaf (feeds the threshold clearance).
    cut: number;
    // which edge the leaf pivots on, and whether it opens toward the viewer (the
    // view is from the outside, so "out" swings toward us). Feeds the hover swing
    // preview; a double-acting leaf previews as out.
    hingesOnLeft: boolean;
    swingsOut: boolean;
    // the leaf's glass lites, in the same coords as the door edges, so the swing
    // preview can carry them with the ghost leaf.
    lites: LiteRect[];
};

export type LiteRect = {left: number; top: number; right: number; bottom: number};

// Hosts one leaf's interactive hardware crosshairs (handle, lock and any other
// placed items) and glass lites as the exact same Things the door-elevation view
// uses, so the frame-grid door face renders — and edits — identically. The leaf
// face, hinges and undercut are still drawn by DoorFace into the shared graphics;
// only the hardware + lites (with their dimensions) live here. Positioned per-draw
// via update(); the Things draw in leaf-local coords, so their graphic roots sit
// at the leaf's top-left.
class DoorLeafFace {
    private liteThings: DoorLiteThing[] = [];
    // placed hardware-item crosshairs (door preps, incl. handle + lock). Hosted per
    // leaf, filtered by the item's per-leaf qty — an item placed on both leaves of a
    // pair (activeQty + inactiveQty) draws on each, mirrored onto the inactive leaf.
    private crosshairThings: HardwareCrosshairThing[] = [];
    // the lite ids currently built, so syncLites() only rebuilds on add/remove.
    private liteSignature = "";
    // the door-crosshair placement ids currently built, so syncCrosshairs() only
    // rebuilds on add/remove.
    private crosshairSignature = "";
    private readonly geom: LeafGeom = {left: 0, top: 0, width: 0, height: 0};

    constructor(private elev: ElevationApp, private container: Container, private leaf: LeafKind) {
        this.syncLites();
        this.syncCrosshairs();
    }

    private leafSize = () => ({width: this.geom.width, height: this.geom.height});

    // repaint the hosted Things in place, without a grid reflow. A hardware or
    // lite value edit never changes the leaf/cell size, so this is all that's
    // needed (and it's drag-safe: no teardown mid-gesture).
    private redraw = () => {
        for (const t of this.liteThings) if (!t.destroyed) t.draw();
        for (const t of this.crosshairThings) if (!t.destroyed) t.draw();
    };

    // (re)build the lite Things to match this leaf's current lite set. A no-op
    // unless a lite was added/removed, so it's safe to call every draw.
    private syncLites() {
        const lites = this.elev.doorSpec.litesFor(this.leaf);
        const signature = lites.map(l => l.id).join(",");
        if (signature === this.liteSignature) return;

        for (const t of this.liteThings) if (!t.destroyed) t.destroy();
        this.liteThings = [];

        for (const lite of lites) {
            const thing = new DoorLiteThing({
                elev: this.elev,
                lite,
                leafSize: this.leafSize,
                onEdit: this.redraw,
                onSelect: (additive) => this.select({type: "door-lite", id: lite.id, leaf: lite.leaf}, additive),
            });
            this.container.addChild(thing.graphic);
            this.liteThings.push(thing);
        }

        this.liteSignature = signature;
    }

    // (re)build the door-crosshair Things to match the placed-hardware list (door
    // preps) that prep this leaf. A no-op unless the set changed (add/remove, or a
    // per-leaf qty flip), so it's safe to call every draw.
    private syncCrosshairs() {
        // the frame-only views draw no door hardware.
        if (this.elev.viewSettings.frameOnly()) return;

        const active = this.leaf === "active";
        const hw = this.elev.hardwareGroup?.hardware ?? [];

        // this leaf's own door placements (an item on both leaves has one per leaf, so the two draw
        // and drag independently).
        const placements = this.elev.hardware.list.filter(p => placedOnLeaf(p, active));

        const signature = placements.map(p => p.id).join(",");
        if (signature === this.crosshairSignature) return;

        for (const t of this.crosshairThings) if (!t.destroyed) t.destroy();
        this.crosshairThings = [];

        for (const placement of placements) {
            const item = hw.find(h => h.product === placement.productId);
            if (!item) continue;

            const thing = new HardwareCrosshairThing({
                elev: this.elev,
                item: placement,
                label: item.productCode.slice(0, 6),
                product: item,
                // measure to the leaf box (leaf-local coords, like the lites).
                target: {rect: () => ({x0: 0, y0: 0, x1: this.geom.width, y1: this.geom.height})},
                onSelect: (additive) => this.select({type: "hardware-item", id: placement.id, kind: placement.target, productId: placement.productId}, additive),
            });
            this.container.addChild(thing.graphic);
            this.crosshairThings.push(thing);
        }

        this.crosshairSignature = signature;
    }

    private select(sel: MdSelection, additive: boolean) {
        this.elev.viewSettings.selectItem(sel, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    // called each DoorFace draw for this leaf: pick up any lite add/remove,
    // refresh the leaf geometry, then reposition + redraw the hosted Things.
    update(geom: LeafGeom) {
        this.syncLites();
        this.syncCrosshairs();

        this.geom.left = geom.left;
        this.geom.top = geom.top;
        this.geom.width = geom.width;
        this.geom.height = geom.height;

        // resolve each lite against the current leaf size before drawing, so locked offsets
        // (e.g. right/bottom) hold when the door differs from the design's size.
        for (const t of this.liteThings) {
            if (!t.destroyed) t.getLite().normalize(geom.width, geom.height);
        }

        for (const t of [...this.liteThings, ...this.crosshairThings]) {
            if (t.destroyed) continue;
            t.graphic.position.set(geom.left, geom.top);
            t.draw();
        }
    }

    destroy() {
        for (const t of [...this.liteThings, ...this.crosshairThings]) {
            if (!t.destroyed) t.destroy();
        }
        this.liteThings = [];
        this.crosshairThings = [];
    }
}

// hover feedback for the door: a small swing badge sits at the top hinge-side
// corner of each leaf; while the pointer is over a badge, a translucent copy of
// each leaf eases open a little about its hinge edge, holds while it stays, and
// eases shut when it leaves — so the swing side reads at a glance. Both the badge
// and the ghost draw into their own graphics mounted in the item's dimension
// container (above the leaf); only the badge is interactive.
export class SwingPreview {
    private ghost: Graphics | null = null;
    private icon: Graphics | null = null;
    private ticker: Ticker | null = null;
    private hovered = false;
    // the current open angle (radians); eased toward open/closed by tick().
    private angle = 0;
    private leaves: LeafRect[] = [];
    private destroyed = false;

    // refresh the leaf geometry after a draw pass; mounts the ghost + badge
    // graphics on first call. Re-renders in place if a swing is mid-animation.
    update(props: Pick<BlockItemDrawProps, "dimensionContainer" | "elev">, leaves: LeafRect[]) {
        if (this.destroyed) return;
        this.leaves = leaves;

        if (!this.ghost) {
            const g = new Graphics();
            g.eventMode = "none";
            props.dimensionContainer.addChild(g);
            this.ghost = g;
        }

        if (!this.icon) {
            const g = new Graphics();
            g.eventMode = "static";
            g.cursor = "pointer";
            g.on("pointerover", () => this.setHovered(true));
            g.on("pointerout", () => this.setHovered(false));
            props.dimensionContainer.addChild(g);
            this.icon = g;
        }

        // a screen-only affordance: keep it off the printout (external-dimensions
        // mode, like the inline dimensions).
        this.drawIcon(props.elev.viewSettings.externalDimensions.value);
        if (this.angle > 0) this.render();
    }

    // the trigger badge at each leaf's top hinge corner: a circle holding a mini
    // plan-view swing glyph (door line + swing arc), mirrored to the hinge side.
    // The filled circle doubles as the hover hit shape.
    private drawIcon(hidden: boolean) {
        const g = this.icon;
        if (!g || g.destroyed) return;
        g.clear();
        if (hidden) return;

        for (const leaf of this.leaves) {
            const w = leaf.doorRight - leaf.doorLeft;
            if (w <= 0) continue;
            const dir = leaf.hingesOnLeft ? 1 : -1;
            const hx = leaf.hingesOnLeft ? leaf.doorLeft : leaf.doorRight;
            const cx = hx + dir * (SWING_ICON_MARGIN_X + SWING_ICON_RADIUS);
            const cy = leaf.doorTop + SWING_ICON_MARGIN_Y + SWING_ICON_RADIUS;

            g.lineStyle(1.5, lineBlueHeavy, 0.9);
            g.beginFill(0xffffff, 0.9);
            g.drawCircle(cx, cy, SWING_ICON_RADIUS);
            g.endFill();

            // glyph: hinge at the badge's bottom hinge-side, closed-door line
            // running across, quarter arc up to the open position.
            const a = SWING_ICON_RADIUS * 0.45;
            const px = cx - dir * a;
            const py = cy + a;
            g.moveTo(px, py);
            g.lineTo(px + dir * 2 * a, py);
            g.moveTo(px + dir * 2 * a, py);
            g.arc(px, py, 2 * a, dir === 1 ? 0 : Math.PI, dir === 1 ? -Math.PI / 2 : Math.PI * 1.5, dir === 1);

            // arrowhead at the arc's open end.
            const ex = px;
            const ey = py - 2 * a;
            g.lineStyle(0);
            g.beginFill(lineBlueHeavy, 0.9);
            g.drawPolygon([
                ex - dir * a * 0.5, ey,
                ex + dir * a * 0.15, ey - a * 0.4,
                ex + dir * a * 0.15, ey + a * 0.4,
            ]);
            g.endFill();
        }
    }

    private setHovered(hovered: boolean) {
        if (this.destroyed || this.hovered === hovered) return;
        this.hovered = hovered;
        this.startTicker();
    }

    private startTicker() {
        if (this.ticker) return;
        const ticker = new Ticker();
        ticker.add(this.tick);
        ticker.start();
        this.ticker = ticker;
    }

    private stopTicker() {
        if (!this.ticker) return;
        this.ticker.remove(this.tick);
        this.ticker.destroy();
        this.ticker = null;
    }

    private tick = () => {
        const dt = this.ticker?.deltaMS ?? 16;
        const target = this.hovered ? SWING_MAX_ANGLE : 0;

        // exponential ease toward the target; snap and idle the ticker once there,
        // so a held-open (or closed) ghost costs nothing per frame.
        this.angle += (target - this.angle) * (1 - Math.exp(-dt / SWING_EASE_MS));
        if (Math.abs(target - this.angle) < 0.005) {
            this.angle = target;
            this.stopTicker();
        }
        this.render();
    };

    // the ghost leaf, projected: the hinge edge stays put, the free edge moves in
    // by cos(angle) of the width and stretches vertically a touch when the leaf
    // comes toward the viewer (out-swing) or shrinks when it goes away (in-swing).
    // At angle 0 it coincides with the drawn leaf. The lites ride along, projected
    // the same way and cut out of the ghost so they read as glass.
    private render() {
        const g = this.ghost;
        if (!g || g.destroyed) return;
        g.clear();
        if (this.angle <= 0) return;

        const cos = Math.cos(this.angle);
        const sin = Math.sin(this.angle);
        for (const leaf of this.leaves) {
            const w = leaf.doorRight - leaf.doorLeft;
            const h = leaf.doorBottom - leaf.doorTop;
            if (w <= 0 || h <= 0) continue;
            const hx = leaf.hingesOnLeft ? leaf.doorLeft : leaf.doorRight;
            const dir = leaf.hingesOnLeft ? 1 : -1;
            const cy = (leaf.doorTop + leaf.doorBottom) / 2;
            const vy = w * sin * SWING_PERSPECTIVE * (leaf.swingsOut ? 1 : -1);

            // a point on the closed leaf, moved with the swing: its distance from
            // the hinge foreshortens by cos, and its vertical offset from the leaf's
            // centre line grows in proportion to that distance (vy at the free edge).
            const project = (x: number, y: number): [number, number] => {
                const u = dir * (x - hx) / w;
                return [hx + dir * u * w * cos, cy + (y - cy) * (1 + u * vy * 2 / h)];
            };
            const quad = (r: LiteRect) => [
                ...project(r.left, r.top),
                ...project(r.right, r.top),
                ...project(r.right, r.bottom),
                ...project(r.left, r.bottom),
            ];

            g.lineStyle(2, lineBlueHeavy, 0.7);
            g.beginFill(lineBlueHeavy, 0.12);
            g.drawPolygon(quad({left: leaf.doorLeft, top: leaf.doorTop, right: leaf.doorRight, bottom: leaf.doorBottom}));
            for (const lite of leaf.lites) {
                g.beginHole();
                g.drawPolygon(quad(lite));
                g.endHole();
            }
            g.endFill();

            // the hole has no stroke of its own
            g.lineStyle(1.5, lineBlueHeavy, 0.7);
            for (const lite of leaf.lites) {
                g.drawPolygon(quad(lite));
            }
        }
    }

    destroy() {
        this.destroyed = true;
        this.stopTicker();
        // the graphics may already be gone with the item's container on a grid teardown.
        if (this.ghost && !this.ghost.destroyed) this.ghost.destroy();
        this.ghost = null;
        if (this.icon && !this.icon.destroyed) this.icon.destroy();
        this.icon = null;
    }
}

export class DoorFace {
    // DoorFace has no elev at construction (it arrives per-draw), so its
    // dimensions are created lazily on the first draw and reused thereafter.
    private pairGapDim: LinearDimension | null = null;
    private thresholdDim: LinearDimension | null = null;

    // the per-leaf hosts for the interactive hardware + lites, keyed by leaf.
    // Built lazily in renderDoor and torn down when a leaf stops being shown
    // (e.g. a pair collapses to a single door).
    private leafFaces = new Map<LeafKind, DoorLeafFace>();
    // leaves rendered in the current draw, so stale hosts can be pruned after.
    private drawnLeaves = new Set<LeafKind>();
    // the leaf boxes drawn this pass (item-local coords), keyed by leaf, so the
    // hardware export can dimension the door-mounted hardware hosted on them.
    private leafRects = new Map<LeafKind, LeafRect>();

    private swing = new SwingPreview();

    // "slot - opening" on the primary's door of a multi-opening frame; none on a single-door frame.
    private slotLabel = new SlotLabel();

    draw(props: BlockItemDrawProps) {
        const g = props.graphics;
        g.clear();
        this.drawnLeaves.clear();
        this.leafRects.clear();

        const elev = props.elev;
        const frame = elev.frameSpec;

        this.slotLabel.draw(props, frame.slots.length > 0 ? `${frame.primarySlot} - ${elev.opening?.name ?? ""}` : null);

        // assume we're on the outside

        if(!frame.isPair) {
            // draw single door
            const leaf = this.renderDoor(props, true, elev, props.startX, props.startY, props.endX, props.endY, null);
            this.drawDimensions(props, leaf, null);
            this.swing.update(props, [...this.leafRects.values()]);
            this.pruneLeaves();
            return;
        }

        // draw a pair of doors, split at the real meeting gap. The active leaf
        // takes activeDoorWidth against its jamb; the gap between the two leaves
        // is pairDoorGap, so the drawn leaves meet at the true gap and it can be
        // dimensioned.
        const width = props.endX - props.startX;
        const activeWidth = frame.activeDoorWidth.get();
        const activeIsOnLeft = getSwingSide(elev, true).side === "left";
        const gapX = frame.pairDoorGap.get();

        const activeEdge = activeIsOnLeft ? activeWidth : width - activeWidth;
        const gapLeft = props.startX + (activeIsOnLeft ? activeEdge : activeEdge - gapX);
        const gapRight = props.startX + (activeIsOnLeft ? activeEdge + gapX : activeEdge);

        // the left leaf meets on its right edge, the right leaf on its left edge;
        // neither meeting edge gets a jamb/stop gap — the pairDoorGap is the gap.
        const left = this.renderDoor(props, activeIsOnLeft, elev, props.startX, props.startY, gapLeft, props.endY, "right");
        const right = this.renderDoor(props, !activeIsOnLeft, elev, gapRight, props.startY, props.endX, props.endY, "left");

        this.drawDimensions(props, activeIsOnLeft ? left : right, {gapLeft, gapRight});
        this.swing.update(props, [...this.leafRects.values()]);
        this.pruneLeaves();
    }

    // leftX/topY/rightX/bottomY are the frame opening bounds with no gaps applied.
    // We inset to the actual door leaf: jamb gap on the hinge side, stop gap on
    // the latch side, top gap + overcut at the head, and bottom gap + undercut at
    // the sill (mirrors MdDoorSpec.actualHeight). `meetingSide` marks a pair leaf's
    // inner edge, which sits exactly on the bound (no gap) so the two leaves meet
    // across the pairDoorGap.
    renderDoor(props: BlockItemDrawProps, active: boolean, elev: ElevationApp, leftX: number, topY: number, rightX: number, bottomY: number, meetingSide: "left" | "right" | null): LeafRect {
        const g = props.graphics;
        const frame = elev.frameSpec;
        const door = elev.doorSpec;

        const swing = getSwingSide(elev, active);
        const hingesOnLeft = swing.side === "left";

        const jambGap = frame.frameJambToDoorGap.get();
        const stopGap = frame.frameStopToDoorGap.get();
        const overcut = door.overcut.get();
        const undercut = door.undercut.get();

        const leftInset = meetingSide === "left" ? 0 : (hingesOnLeft ? jambGap : stopGap);
        const rightInset = meetingSide === "right" ? 0 : (hingesOnLeft ? stopGap : jambGap);

        const doorLeft = leftX + leftInset;
        const doorRight = rightX - rightInset;
        const doorWidth = doorRight - doorLeft;

        // an overcut trims the top of the leaf, an undercut the bottom. leafTop is the
        // nominal top of the leaf (the hinge reference); the drawn face starts an
        // overcut below it and ends an undercut above the bottom gap.
        //
        // The door undercut is the clearance to the *true floor* (nominalHeight below
        // the head). But bottomY (the frame opening bottom) already sits `frameUndercut`
        // above that floor — the frame is cut short in this view (see MdFrameSpec's door
        // height) — so only the net (door - frame) undercut opens up below the frame
        // sill. Clamp at 0 so a door undercut shorter than the frame's just rests the
        // leaf on the sill rather than hanging below it.
        const frameUndercut = frame.undercutFrame.get();
        const netUndercut = Math.max(0, undercut - frameUndercut);
        const leafTop = topY + door.topGap.get();
        const doorTop = leafTop + overcut;
        const doorBottom = bottomY - door.bottomGap.get() - netUndercut;

        // door leaf
        g.lineStyle(2, lineBlueHeavy, 1);
        g.beginFill(panelMetalGrey, 1);
        g.drawRect(doorLeft, doorTop, doorWidth, doorBottom - doorTop);
        g.endFill();

        // overcut marker: a light-red dashed rectangle for the material trimmed off
        // the top of the leaf.
        if (overcut > 0) {
            g.lineStyle(0);
            g.beginFill(lineRedGrey, 0.4);
            g.drawRect(doorLeft, leafTop, doorWidth, overcut);
            g.endFill();
            g.lineStyle(1, lineRed, 1);
            drawDashedRect(g, doorLeft, leafTop, doorWidth, overcut);
        }

        // undercut: the trimmed region isn't drawn here (only the Locations view
        // shows that) — just a red line marking the bottom of the door.
        if (undercut > 0) {
            g.lineStyle(1, lineRed, 1);
            g.moveTo(doorLeft, doorBottom);
            g.lineTo(doorRight, doorBottom);
        }

        // hinges march down the hinge-side jamb. `top` is measured from the top
        // of the leaf, centered on the knuckle. The frame-only views omit them.
        if (!elev.viewSettings.frameOnly()) {
            const hingeEdge = hingesOnLeft ? doorLeft : doorRight;
            const hinges = elev.hardware.hinges.list;

            for (let i = 0; i < hinges.length; i++) {
                const hinge = hinges[i];
                // offsetTop is frame-referenced (from the head), so it's measured from the
                // nominal leaf top — the overcut shifts the face down, not the hinges.
                const cy = leafTop + hinge.offsetTop.get();
                const height = elev.hardware.hinges.hingeHeightAt(i)
                g.lineStyle(1, lineBlueHeavy, 0.5);
                g.beginFill(lineBlueHeavy, 0.5);
                g.drawRect(hingeEdge - HINGE_WIDTH / 2, cy - height / 2, HINGE_WIDTH, height);
                g.endFill();
            }
        }

        // the interactive glass lites + hardware (handle/lock), with their
        // dimensions, are hosted by a DoorLeafFace — the same Things the
        // door-elevation view uses. They sit on top of the leaf face (their
        // graphics are children of the dimension container, drawn after the leaf).
        const leaf: LeafKind = active ? "active" : "inactive";
        this.drawnLeaves.add(leaf);
        let face = this.leafFaces.get(leaf);
        if (!face) {
            face = new DoorLeafFace(elev, props.dimensionContainer, leaf);
            this.leafFaces.set(leaf, face);
        }
        face.update({
            left: doorLeft,
            top: doorTop,
            width: doorWidth,
            height: doorBottom - doorTop,
        });

        // read after face.update, which resolved the lites against this leaf size.
        const lites: LiteRect[] = door.litesFor(leaf).map(l => {
            const left = doorLeft + l.offsetLeft.get();
            const top = doorTop + l.offsetTop.get();
            return {left, top, right: left + l.width.get(), bottom: top + l.height.get()};
        });

        // the threshold clearance reads the bottom (net-undercut) trim only — the
        // overcut is at the top and doesn't affect the gap under the door.
        const rect: LeafRect = {doorLeft, doorRight, doorTop, doorBottom, cut: netUndercut, hingesOnLeft, swingsOut: swing.direction !== "in", lites};
        this.leafRects.set(leaf, rect);
        return rect;
    }

    // the active door leaf's box in item-local coords, or null if no door drawn.
    // Door hardware crosshairs are hosted on the active leaf, positioned from its
    // top-left; the hardware export reads this to dimension them.
    getActiveLeafRect(): LeafRect | null {
        return this.leafRects.get("active") ?? null;
    }

    // tear down any leaf host that wasn't rendered this draw (e.g. a pair
    // collapsed to a single door leaves a stale "inactive" host).
    private pruneLeaves() {
        for (const [leaf, face] of this.leafFaces) {
            if (!this.drawnLeaves.has(leaf)) {
                face.destroy();
                this.leafFaces.delete(leaf);
            }
        }
    }

    // draw the pair-gap and threshold-clearance dimensions, gated on the "show
    // all dimensions" view toggle. `activeLeaf` is the leaf the threshold
    // dimension anchors to; `pair` carries the meeting-gap edges when the opening
    // is a pair, else null.
    private drawDimensions(props: BlockItemDrawProps, activeLeaf: LeafRect, pair: {gapLeft: number; gapRight: number} | null) {
        const elev = props.elev;
        const app = elev.draw;

        if (!this.pairGapDim) this.pairGapDim = new LinearDimension(props.dimensionContainer, app);
        if (!this.thresholdDim) this.thresholdDim = new LinearDimension(props.dimensionContainer, app);

        if (!elev.viewSettings.dimShowAll.value) {
            this.pairGapDim.hide();
            this.thresholdDim.hide();
            return;
        }

        // the gap between the two leaves (horizontal), only for a pair.
        if (pair) {
            const pairDoorGap = elev.frameSpec.pairDoorGap;
            this.pairGapDim.draw({
                axis: "horizontal",
                from: pair.gapLeft,
                to: pair.gapRight,
                cross: activeLeaf.doorTop + PAIR_GAP_DIM_OFFSET,
                value: pairDoorGap.get(),
                modified: pairDoorGap.isModified(),
                onSet: (v) => {
                    pairDoorGap.userSet(v, {debug: "pair-gap-dim-edit"});
                    props.relayout();
                },
                onClear: () => {
                    pairDoorGap.userClearModified();
                    props.relayout();
                },
            });
        } else {
            this.pairGapDim.hide();
        }

        // clearance from the base of the leaf down to the threshold / floor
        // (vertical). The visible gap is bottomGap + under/overcut; editing it
        // drives bottomGap (the under/overcut stay fixed).
        const door = elev.doorSpec;
        const clearance = door.bottomGap.get() + activeLeaf.cut;
        this.thresholdDim.draw({
            axis: "vertical",
            from: activeLeaf.doorBottom,
            to: props.endY,
            cross: activeLeaf.doorLeft + THRESHOLD_DIM_OFFSET,
            value: clearance,
            modified: door.bottomGap.isModified(),
            onSet: (v) => {
                door.bottomGap.userSet(v - activeLeaf.cut, {debug: "threshold-gap-dim-edit"});
                props.relayout();
            },
            onClear: () => {
                door.bottomGap.userClearModified();
                props.relayout();
            },
        });
    }

    destroy() {
        for (const face of this.leafFaces.values()) face.destroy();
        this.leafFaces.clear();
        this.swing.destroy();
        this.slotLabel.destroy();
        this.pairGapDim?.destroy();
        this.thresholdDim?.destroy();
    }
}
